import { createFileRoute, Link } from "@tanstack/react-router";
import { useUser } from "@clerk/clerk-react";
import { useState, useEffect, useRef } from "react";
import { getUserFromClerkId, useMarkDashboardSeen } from "@/hooks/useUser";
import { getDailyTip, getScamOfTheDay } from "@/hooks/useDailyTipScam";
import { HiOutlineShieldCheck, HiOutlineChatAlt2 } from "react-icons/hi";
import { FiUsers } from "react-icons/fi";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import VideoPlayer from "@/components/VideoPlayer";
import { useMissions } from "@/hooks/useMissions";
import { FamilyAvatarRail } from "@/components/family/FamilyAvatarRail";
import EmergencyCard from "@/components/EmergencyCard";
import { useFamilyProgress } from "@/hooks/useFamilyProgress";
import { ProtectionScoreCard } from "@/components/home/GuardianLevelCard";
import { UpNextCardE } from "@/components/home/UpNextCard";
import { QuickActionCardE } from "@/components/home/QuickActionCard";
import { MainInlineScamCheckerCard } from "@/components/home/InlineScamCheckerCard";
import { ScamRadarWithFlagsCard } from "@/components/home/ScamRadarCard";
import { useCourseProgress } from "@/hooks/useCourseProgress";

export const Route = createFileRoute("/_auth/")({
  component: HomePage,
});

function HomePage() {
  const { user: clerkUser } = useUser();
  const userQuery = getUserFromClerkId(clerkUser?.id || "", { enabled: !!clerkUser?.id });
  const dailyTipQuery = getDailyTip();
  const missionsQuery = useMissions();
  const scamOfTheDayQuery = getScamOfTheDay();
  const courseProgress = useCourseProgress();

  const isSponsor = !!userQuery?.data && !userQuery.data.parentUserId;
  const isChild = !!userQuery?.data?.parentUserId;
  const familyQuery = useFamilyProgress({ enabled: isSponsor });
  // Tighter check: only sponsors who have paid for additional seats see
  // the family widget. Solo users (totalSeats === 1) get a clean welcome
  // strip with no empty right slot.
  const hasFamilyPlan =
    isSponsor && !!familyQuery.data && familyQuery.data.totalSeats > 1;

  const userName = userQuery?.data?.name || "there";

  // Greeting: "Welcome aboard" on the user's very first dashboard visit,
  // "Welcome back" after that, "Welcome" while still onboarding. We freeze the
  // first-visit decision in a ref the moment data arrives so it can't flip
  // mid-session, then stamp it server-side once.
  const onboardingComplete = userQuery?.data?.onboardingStep === "COMPLETE";
  const firstVisitRef = useRef<boolean | null>(null);
  if (firstVisitRef.current === null && userQuery?.data && onboardingComplete) {
    firstVisitRef.current = (userQuery.data.dashboardSeenAt ?? null) === null;
  }
  const isFirstVisit = firstVisitRef.current === true;

  const markDashboardSeen = useMarkDashboardSeen();
  const markedSeenRef = useRef(false);
  useEffect(() => {
    if (isFirstVisit && !markedSeenRef.current) {
      markedSeenRef.current = true;
      markDashboardSeen.mutate();
    }
  }, [isFirstVisit, markDashboardSeen]);

  const greeting = !onboardingComplete
    ? "Welcome"
    : isFirstVisit
      ? "Welcome aboard"
      : "Welcome back";
  const guardianPoints = Math.max(0, userQuery?.data?.exp ?? 0);
  const guardianLevel = Math.floor(guardianPoints / 100) + 1;
  const levelProgress = guardianPoints % 100;
  const levelProgressPercent = Math.min(100, Math.round((levelProgress / 100) * 100));

  const [isTipOpen, setIsTipOpen] = useState(false);

  // Tip modal a11y: Escape closes, focus moves to the close button on open
  // and returns to the trigger on close, and the page doesn't scroll behind
  // the dialog. (Pattern from SubscriptionModal, the app's modal standard.)
  const tipCloseRef = useRef<HTMLButtonElement | null>(null);
  const tipTriggerRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!isTipOpen) return;
    tipCloseRef.current?.focus();
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsTipOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      tipTriggerRef.current?.focus();
    };
  }, [isTipOpen]);

  const tipShortDescription =
    dailyTipQuery.data?.shortDescription ||
    "Take a few minutes to check for new data breaches.";

  // Score signals — same shape as Variant Main in the home-lab. Setup,
  // walkthrough, and Fundamentals course progress drive the Guardian Score.
  // Once Defence Tracker lands, swap these inputs for that source of truth.
  // Missions are matched by their server-defined step ids (stable), with the
  // title as fallback: a copy change must not silently break the checklist.
  const setupMission = missionsQuery.data?.find(
    (m) => m.steps?.some((s) => s.id === "finish-setup") || m.title === "Complete setup",
  );
  const tourMission = missionsQuery.data?.find(
    (m) => m.steps?.some((s) => s.id === "finish-tour") || m.title === "Complete the tour",
  );
  const setupComplete =
    setupMission?.status === "COMPLETED" || userQuery?.data?.onboardingStep === "COMPLETE";
  const tourComplete = tourMission?.status === "COMPLETED";
  const coursePct = Math.round(
    (courseProgress.completedChapters / Math.max(1, courseProgress.totalChapters)) * 100,
  );

  const checklist: { label: string; status: "good" | "progress" | "info"; percent?: number }[] = [
    { label: "Account setup", status: setupComplete ? "good" : "info" },
    { label: "Guided walkthrough", status: tourComplete ? "good" : "info" },
    {
      label: "Fundamentals guide",
      status: courseProgress.isComplete ? "good" : "progress",
      percent: coursePct,
    },
  ];

  const familyRailProps = {
    sponsor: {
      name: userQuery.data?.name,
      email: clerkUser?.emailAddresses?.[0]?.emailAddress || "",
      avatarConfig: userQuery.data?.avatarConfig,
      imageUrl: clerkUser?.imageUrl,
    },
  };

  const cleanQuillHtml = (html: string) => {
    let result = html.replace(/<span class="ql-ui">.*?<\/span>/g, "");
    result = result.replace(/<p>(\s|&nbsp;)*<\/p>/g, "");
    return result;
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="max-w-6xl mx-auto pt-4 pb-16 px-4 sm:px-6">
        <div className="space-y-4">
          {/* Welcome strip + daily tip on the left, family plan widget on
              the right. The family widget uses the "fieldset" chrome —
              label embedded in the top border. 2-col grid kicks in at
              480px so the rail stays anchored top-right on every tablet
              (incl. iPad Mini at 744–768px) and only stacks at phone-
              portrait widths. Greeting column uses minmax(0,1fr) so it
              shrinks and wraps text as needed. */}
          <section className="px-1 pt-3 pb-4">
            <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-[minmax(0,1fr)_auto] min-[480px]:items-center min-[480px]:gap-5 sm:gap-6 lg:gap-8">
              <div className="min-w-0">
                <h1 className="text-[clamp(1.6rem,4vw,3rem)] font-semibold leading-[1.06] tracking-tight text-slate-900">
                  {greeting}, {userName}!
                </h1>
                <p className="mt-2 max-w-[60ch] text-[17px] leading-relaxed text-slate-600">
                  <span className="font-semibold text-slate-900">Daily tip:</span>{" "}
                  {tipShortDescription}{" "}
                  {dailyTipQuery.data && (
                    <button
                      type="button"
                      ref={tipTriggerRef}
                      onClick={() => setIsTipOpen(true)}
                      className="cursor-pointer font-medium text-violet-700 underline decoration-violet-300/80 underline-offset-4 transition-colors hover:text-violet-800"
                    >
                      Learn more
                    </button>
                  )}
                </p>
              </div>
              {hasFamilyPlan && (
                <div className="min-[480px]:justify-self-end">
                  {/* Fieldset-style chrome: "Family Plan" label embedded in
                      the top border. The label has bg-[#f5f5f7] (matching
                      the page bg) so it visually cuts the border behind it. */}
                  <div className="relative inline-flex flex-col rounded-2xl border border-slate-200 px-4 pt-5 pb-3">
                    <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1.5 bg-[#f5f5f7] px-2 text-[11.5px] font-medium text-slate-600">
                      <FiUsers className="h-3.5 w-3.5 text-slate-400" />
                      Family Plan
                    </span>
                    <FamilyAvatarRail {...familyRailProps} horizontal />
                  </div>
                </div>
              )}
              {/* Invited members get the same compact rail in the same
                  slot, in member mode so it shows their sponsor +
                  co-members and skips the invite controls. */}
              {isChild && (
                <div className="min-[480px]:justify-self-end">
                  <div className="relative inline-flex flex-col rounded-2xl border border-slate-200 px-4 pt-5 pb-3">
                    <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1.5 bg-[#f5f5f7] px-2 text-[11.5px] font-medium text-slate-600">
                      <FiUsers className="h-3.5 w-3.5 text-slate-400" />
                      Family Plan
                    </span>
                    <FamilyAvatarRail mode="member" horizontal />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Hero: Guardian Level + Up Next */}
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)]">
            <ProtectionScoreCard
              checklist={checklist}
              level={guardianLevel}
              levelProgress={levelProgress}
              levelProgressPercent={levelProgressPercent}
              points={guardianPoints}
              market={userQuery?.data?.market}
            />
            <UpNextCardE market={userQuery?.data?.market} />
          </section>

          {/* Quick Actions: inline Scam Checker + 2 mini cards */}
          <section className="space-y-3">
            <h2 className="px-1 text-[1.05rem] font-semibold tracking-tight text-slate-900">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <MainInlineScamCheckerCard />
              </div>
              <div className="lg:col-span-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <QuickActionCardE
                  to="/footprint"
                  icon={<HiOutlineShieldCheck className="h-6 w-6" />}
                  tone="border border-violet-100 bg-violet-50 text-violet-700"
                  title="Breach Monitor"
                  description="See if your data has been leaked."
                />
                <QuickActionCardE
                  to="/contact-support"
                  icon={<HiOutlineChatAlt2 className="h-6 w-6" />}
                  tone="border border-emerald-100 bg-emerald-50 text-emerald-600"
                  title="Ask for Advice"
                  description="Talk to a real security expert."
                />
              </div>
            </div>
          </section>

          {/* Scam Radar. Red flags are hidden until they come from the scam
              entry itself (scam-library rebuild adds a redFlags field): the
              old hardcoded delivery-scam list could sit beside an unrelated
              scam and teach the wrong heuristics. TD-017. */}
          <ScamRadarWithFlagsCard
            scamOfTheDay={scamOfTheDayQuery.data ?? undefined}
            isLoading={scamOfTheDayQuery.isLoading}
            redFlags={[]}
            showRedFlags={false}
          />

          {/* Emergency Toolkit shortcut. Calm secondary surface — discoverable
              when something has gone wrong, not loud during everyday use. */}
          <EmergencyCard />
        </div>
      </div>

      {/* Daily tip modal — kept from the previous home so the full tip
          content (including video) can be read in place. */}
      {dailyTipQuery.data && isTipOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 backdrop-blur-sm"
          onClick={() => setIsTipOpen(false)}
        >
          <div
            className="relative w-full max-w-2xl overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.22)]"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="daily-tip-title"
          >
            <button
              type="button"
              ref={tipCloseRef}
              className="absolute right-4 top-3.5 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-[28px] font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-violet-500"
              onClick={() => setIsTipOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="border-b border-slate-100/90 px-7 pb-3 pt-4.5">
              <div className="pr-14">
                <h3 id="daily-tip-title" className="text-[22px] font-semibold tracking-tight text-slate-900">
                  {dailyTipQuery.data.title}
                </h3>
              </div>
            </div>
            <div className="prose prose-slate max-h-[68vh] max-w-none overflow-y-auto px-7 pb-6 pt-5 text-[15px] leading-relaxed text-slate-700 prose-p:leading-relaxed">
              {dailyTipQuery.data.videoFile && (
                <div className="mb-5">
                  <VideoPlayer src={dailyTipQuery.data.videoFile} autoPlay />
                </div>
              )}
              {parse(DOMPurify.sanitize(cleanQuillHtml(dailyTipQuery.data.content)))}
            </div>
            <div className="border-t border-slate-100/90 px-7 pb-6 pt-4 flex items-center justify-between gap-3">
              <Link
                to="/learn"
                search={{ tab: undefined, tour: undefined, invitationcode: undefined, __clerk_ticket: undefined }}
                className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-800"
                onClick={() => setIsTipOpen(false)}
              >
                Browse all tips
              </Link>
              <button
                type="button"
                onClick={() => setIsTipOpen(false)}
                className="cursor-pointer rounded-xl bg-slate-900 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
